import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CatchingPokemonIcon from "@mui/icons-material/CatchingPokemon";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const Registration = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedUsername = username.trim().toLowerCase();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedUsername || !trimmedEmail || !trimmedPassword) {
      setError("Bitte fülle alle Felder aus.");
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (trimmedPassword.length < 6) {
      setError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    if (trimmedPassword !== trimmedConfirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setLoading(true);
    try {
      const availabilityResponse = await axios.post(
        "/api/users/check-availability",
        { username: trimmedUsername, email: trimmedEmail }
      );

      const { usernameAvailable, emailAvailable } = availabilityResponse.data;
      if (!usernameAvailable || !emailAvailable) {
        setError(
          !usernameAvailable
            ? "Dieser Benutzername ist bereits vergeben."
            : "Diese E-Mail-Adresse ist bereits registriert."
        );
        return;
      }

      await axios.post("/api/users/registration", {
        username: trimmedUsername,
        email: trimmedEmail,
        password: trimmedPassword,
      });

      setSuccess(true);
    } catch {
      setError("Registrierung fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          px: 2,
          pb: "80px",
          backgroundColor: "background.default",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <CatchingPokemonIcon
            sx={{ fontSize: 56, color: "success.main", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            Registrierung erfolgreich!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Dein Account wurde angelegt. Du kannst dich jetzt einloggen.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate("/login")}
          >
            Zum Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100dvh",
        px: 2,
        pb: "80px",
        backgroundColor: "background.default",
      }}
    >
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <CatchingPokemonIcon sx={{ fontSize: 56, color: "primary.main", mb: 1 }} />
        <Typography variant="h4">PokéGoDex</Typography>
        <Typography variant="body2" color="text.secondary">
          Neuen Account erstellen
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 2.5, sm: 3.5 },
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Benutzername"
            fullWidth
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
          />

          <TextField
            label="E-Mail"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />

          <TextField
            label="Passwort"
            type={passwordVisible ? "text" : "password"}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            helperText="Mindestens 6 Zeichen"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    edge="end"
                  >
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Passwort wiederholen"
            type={passwordVisible ? "text" : "password"}
            fullWidth
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            error={
              confirmPassword.length > 0 && password !== confirmPassword
            }
            helperText={
              confirmPassword.length > 0 && password !== confirmPassword
                ? "Passwörter stimmen nicht überein"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    edge="end"
                  >
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{ mt: 2, mb: 1 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Registrieren"
            )}
          </Button>
        </form>

        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Bereits registriert?{" "}
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Typography
                component="span"
                variant="body2"
                color="primary"
                fontWeight={600}
              >
                Zum Login
              </Typography>
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Registration;
